import * as React from 'react'
import { Box, Typography, Chip, IconButton, Tooltip, Stack, Menu, MenuItem } from '@mui/material'
import { EyeOutlined, EditOutlined, DeleteOutlined, DashOutlined } from '@ant-design/icons'
import { useTheme } from '@mui/material/styles'

// project import
import { useAuth } from 'services/hook/Auth'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { labelFor, BANNER_VIEW_TYPES, BANNER_PAGE_TYPES } from 'constants/banner'
import { getActionItems } from 'utils/util'


const STATUS_COLORS = {
    active:   { bg: '#f6ffed', fg: '#389e0d' },
    inactive: { bg: '#fff2f0', fg: '#cf1322' },
}

export const CellMobileDetails = ({ item, propsDetails }) => {
    const eventbus = useEventBusContext()
    const { checkPermission } = useAuth()

    const actions = propsDetails?.actions || {}
    const colors = STATUS_COLORS[item.status] ?? { bg: '#f5f5f5', fg: '#595959' }

    const handleAction = (event) => {
        if (!event) return
        eventbus.fire(event, item)
    }

    return (
        <Box sx={{ py: 1, width: '100%' , display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: '5px', width: 'fit-content' }}>
                    <Typography variant="body" >{`Title: `}</Typography>
                    <Typography variant="subtitle2" fontWeight={600}>
                        {item.name ?? '—'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: '5px', width: 'fit-content' }}>
                    <Typography variant="body" >{`View Type: `}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {labelFor(BANNER_VIEW_TYPES, item.view_type)}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: '5px', width: 'fit-content' }}>
                    <Typography variant="body" >{`Page Type: `}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {labelFor(BANNER_VIEW_TYPES, item.view_type)} {labelFor(BANNER_PAGE_TYPES, item.page_type)}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: '5px', width: 'fit-content' }}>
                    <Typography variant="body" >{`Status: `}</Typography>
                    <Chip
                        label={item.status === 'active' ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{ backgroundColor: colors.bg, color: colors.fg, fontSize: '12px' }}
                    />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <CellMobileActions item={item} actions={propsDetails?.actions} />
            </Box>
        </Box>
    )
}

/**
 * Generic reusable menu action item
 */
const ActionMenuItem = ({ title, icon, event, payload, onClick, onClose }) => {
    const theme = useTheme()
    const eventbus = useEventBusContext()
    const menuitemsx = { display: 'flex', alignItems: 'center', minHeight: 'auto', p: '8px 16px', '& .anticon': { color: theme.palette.primary.main, mr: '10px' } }

    const handleClick = (e) => {
        e.stopPropagation()

        if (onClick) {
            onClick(e, payload)
        } else {
            eventbus.fire(event, payload)
        }

        if (onClose) {
            onClose()
        }
    }

    return (
        <MenuItem sx={menuitemsx} onClick={handleClick}>
            {icon} {title}
        </MenuItem>
    )
}

/**
 * Render action menu dropdown with permission-based controls
 */
export const CellMobileActions = ({ item, actions }) => {
    const actionitems = getActionItems(item, actions)
    const { checkPermission } = useAuth()
    const [anchorEl, setAnchorEl] = React.useState(null)
    const open = Boolean(anchorEl)

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    return (
        <Stack direction="row" justifyContent="end">
            <IconButton
                id="banner-long-button"
                onClick={(e) => {
                    e.stopPropagation()
                    handleClick(e)
                }}
                sx={{ color: '#000', transform: 'rotate(90deg)' }}
            >
                <DashOutlined />
            </IconButton>

            <Menu
                id="banner-long-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        style: { maxHeight: 48 * 4.5, width: '16ch' }
                    }
                }}
            >
                {actionitems.map((action, idx) =>
                    checkPermission(action.permission) && (
                        <ActionMenuItem key={idx} itle={action.menu} icon={action.icon} event={action.event} payload={action.payload} onClick={action.onClick} onClose={handleClose} />
                    )
                )}
            </Menu>
        </Stack>
    )
}